/**
 * NewsletterDispatchModal — Phase 3 send mechanism (3 modes).
 *
 *   1. Team Preview: Resend ONE email to internal team (Dipali, Nisha,
 *                    Tanisha, Shiva, Nikita, Samar) for sign-off.
 *   2. Bulk Handover: Resend ONE email to bulk operator with HTML body
 *                     only (no CSV — recipient list lives in Zoho
 *                     Campaigns). Default Tanisha; failover Nisha or
 *                     Shiva when she's on leave (Dipali · 8-May-2026).
 *                     Operator triggers the bulk send from Zoho.
 *   3. Personalised: per-member Zoho Desk drafts (Soul-Form-aware).
 *
 * Pre-flight shows the targetable member count.  "Test push (2 members)"
 * caps the impact for the first run.
 */
import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { X, Send, Loader2, ShieldCheck, Users, AlertTriangle, Mail, FileSpreadsheet } from 'lucide-react';

const TEAM_DEFAULTS = [
  'dipali@clubconcierge.in',
  'nisha@clubconcierge.in',
  'tanisha@clubconcierge.in',
  'shiva@clubconcierge.in',
  'nikita.sharma@clubconcierge.in',
  'samar@clubconcierge.in',
];

// Bulk-send operator failover roster (Dipali · 8-May-2026).
// Adding/removing requires a backend allowlist update too — see
// `BULK_OPERATOR_ALLOWLIST` in services/newsletter_email.py.
const BULK_OPERATORS = [
  { key: 'tanisha', label: 'Tanisha (default)', email: 'tanisha@clubconcierge.in' },
  { key: 'nisha',   label: 'Nisha (failover)',  email: 'nisha@clubconcierge.in' },
  { key: 'shiva',   label: 'Shiva (failover)',  email: 'shiva@clubconcierge.in' },
];

export default function NewsletterDispatchModal({ newsletterId, clientId, onClose, onDispatched }) {
  const [mode, setMode] = useState('team_preview');
  const [targets, setTargets] = useState(null);
  const [loadingTargets, setLoadingTargets] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [teamRecipients, setTeamRecipients] = useState(TEAM_DEFAULTS.join(', '));
  const [bulkOperatorKey, setBulkOperatorKey] = useState('tanisha');

  useEffect(() => {
    let mounted = true;
    api.get(`/newsletters/${newsletterId}/dispatch-targets`)
      .then(r => { if (mounted) setTargets(r.data); })
      .catch(e => { if (mounted) setError(e?.response?.data?.detail || e.message); })
      .finally(() => { if (mounted) setLoadingTargets(false); });
    return () => { mounted = false; };
  }, [newsletterId]);

  const operator = BULK_OPERATORS.find(o => o.key === bulkOperatorKey) || BULK_OPERATORS[0];

  const dispatch = async (testOnly) => {
    let confirmText = '';
    if (mode === 'team_preview') {
      const list = teamRecipients.split(',').map(s => s.trim()).filter(Boolean);
      confirmText = `Send a REVIEW copy of this newsletter to ${list.length} internal team members?\n\n${list.join('\n')}`;
    } else if (mode === 'bulk') {
      confirmText = testOnly
        ? `Send a TEST handover email to ${operator.label}?`
        : `Send the HTML to ${operator.label} (${operator.email})?\n\n` +
           `She'll bulk-send to ${targets?.total_targets || '?'} ${(clientId||'').toUpperCase()} members from Zoho Campaigns directly.`;
    } else {
      confirmText = testOnly
        ? `Create 2 personalised Zoho Desk drafts as a test?`
        : `Create ${targets?.total_targets || '?'} personalised Zoho Desk drafts (one per member)?\n\nLSMs will need to review + send each.`;
    }
    if (!testOnly && mode !== 'team_preview' && !window.confirm(confirmText)) return;
    if (testOnly && !window.confirm(confirmText)) return;
    if (mode === 'team_preview' && !window.confirm(confirmText)) return;

    setBusy(true); setError(null); setResult(null);
    try {
      const body = { mode, test_only: testOnly, parallelism: 4 };
      if (mode === 'team_preview') {
        body.custom_recipients = teamRecipients.split(',').map(s => s.trim()).filter(Boolean);
      }
      if (mode === 'bulk') {
        body.bulk_operator_email = bulkOperatorKey;  // backend resolves key → email
      }
      const r = await api.post(`/newsletters/${newsletterId}/dispatch`, body, { timeout: 360000 });
      setResult(r.data);
      if (!testOnly && onDispatched) onDispatched(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const isTeamPreview = mode === 'team_preview';
  const requiresApproval = mode !== 'team_preview';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid="newsletter-dispatch-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: '#E5E1DC' }}>
          <h2 className="font-semibold text-[16px] flex items-center gap-2" style={{ color: '#1B2A4A' }}>
            <Send size={16} style={{ color: '#16653A' }} /> Send Newsletter
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"
            data-testid="newsletter-dispatch-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-wider" style={{ color: '#6B7280' }}>Mode</label>
            <div className="grid grid-cols-1 gap-2 mt-1">
              <ModeCard
                active={mode === 'team_preview'}
                onClick={() => setMode('team_preview')}
                icon={<Mail size={14} />}
                title="Team Preview"
                subtitle="Send a review copy to the internal team for sign-off. Works at any status."
                testId="dispatch-mode-team-preview"
              />
              <ModeCard
                active={mode === 'bulk'}
                onClick={() => setMode('bulk')}
                icon={<FileSpreadsheet size={14} />}
                title="HTML Handover (recommended for routine months)"
                subtitle="ONE email with rendered HTML to the bulk operator (Tanisha by default; Nisha/Shiva failover). Operator triggers the bulk send from Zoho Campaigns directly. Recipient list lives in Zoho — no CSV needed."
                testId="dispatch-mode-bulk"
              />
              <ModeCard
                active={mode === 'personalised'}
                onClick={() => setMode('personalised')}
                icon={<Users size={14} />}
                title="Personalised Drafts"
                subtitle="Per-member Zoho Desk draft (Soul-Form-reordered + first-name opening). LSMs send each one. Use for high-stakes months."
                testId="dispatch-mode-personalised"
              />
            </div>
          </div>

          {isTeamPreview ? (
            <div>
              <label className="text-[10px] uppercase tracking-wider" style={{ color: '#6B7280' }}>
                Recipients (comma-separated emails)
              </label>
              <textarea value={teamRecipients} onChange={(e) => setTeamRecipients(e.target.value)}
                rows={3}
                className="w-full mt-1 px-2 py-1.5 border rounded text-[12px] font-mono"
                style={{ borderColor: '#E5E1DC' }}
                data-testid="dispatch-team-recipients" />
            </div>
          ) : (
            <div className="p-3 rounded-md border space-y-3" style={{ borderColor: '#E5E1DC', background: '#F7F5F2' }}>
              <div className="flex items-center gap-2 text-[12px]" style={{ color: '#1B2A4A' }}>
                <Users size={13} />
                {loadingTargets ? (
                  <span>Loading targets…</span>
                ) : targets ? (
                  <>
                    <strong>{targets.total_targets}</strong> {clientId.toUpperCase()} members
                    <span className="text-[11px]" style={{ color: '#6B7280' }}>
                      (with valid email · not opted out)
                    </span>
                  </>
                ) : (
                  <span style={{ color: '#A32D2D' }}>Could not load targets.</span>
                )}
              </div>
              {targets?.sample?.length > 0 && (
                <div className="text-[11px]" style={{ color: '#6B7280' }}>
                  First 3: {targets.sample.slice(0, 3).map(s => s.name).join(' · ')}
                </div>
              )}
              {mode === 'bulk' && (
                <div className="pt-2 border-t" style={{ borderColor: '#E5E1DC' }}>
                  <label className="text-[10px] uppercase tracking-wider" style={{ color: '#6B7280' }}>
                    Send HTML to
                  </label>
                  <select
                    value={bulkOperatorKey}
                    onChange={(e) => setBulkOperatorKey(e.target.value)}
                    data-testid="bulk-operator-select"
                    className="mt-1 px-3 py-1.5 border rounded text-[12px] w-full"
                    style={{ borderColor: '#E5E1DC', background: 'white' }}
                  >
                    {BULK_OPERATORS.map(o => (
                      <option key={o.key} value={o.key}>{o.label} · {o.email}</option>
                    ))}
                  </select>
                  <p className="text-[10px] mt-1" style={{ color: '#6B7280' }}>
                    Pick failover when Tanisha is on leave. Recipient list stays in Zoho — only the HTML is sent.
                  </p>
                </div>
              )}
            </div>
          )}

          {result && (
            <div className="p-3 rounded-md text-[12px]"
              style={{ background: result.failed > 0 ? '#FEF2EE' : '#DCFCE7',
                       color: result.failed > 0 ? '#92400E' : '#166534' }}
              data-testid="newsletter-dispatch-result">
              <strong>Done.</strong> Mode: <code>{result.mode}</code>.
              {result.mode === 'team_preview' && (
                <> Sent to <strong>{result.total_targets}</strong> reviewers.</>
              )}
              {result.mode === 'bulk' && (
                <> Handover email sent to <strong>{result.operator_email}</strong>.
                  Audience: <strong>{result.total_targets}</strong> {result.total_targets === 1 ? 'member' : 'members'}
                  {' '}(list in Zoho).
                </>
              )}
              {result.mode === 'personalised' && (
                <> Pushed: <strong>{result.pushed}</strong>
                  {result.failed > 0 && <> · Failed: <strong>{result.failed}</strong></>}
                  {result.skipped > 0 && <> · Skipped: <strong>{result.skipped}</strong></>}.
                </>
              )}
            </div>
          )}

          {error && (
            <div className="text-[12px] px-3 py-2 rounded-md flex items-start gap-2"
              style={{ background: '#FEE2E2', color: '#7a1f1f' }}>
              <AlertTriangle size={13} className="mt-0.5" /> {error}
            </div>
          )}

          <div className="flex items-center justify-between pt-2 border-t" style={{ borderColor: '#E5E1DC' }}>
            {requiresApproval ? (
              <button onClick={() => dispatch(true)} disabled={busy || loadingTargets}
                className="text-[12px] px-3 py-1.5 rounded-md border hover:bg-gray-50 inline-flex items-center gap-1 disabled:opacity-50"
                style={{ borderColor: '#1C304A', color: '#1B2A4A' }}
                data-testid="newsletter-dispatch-test">
                <ShieldCheck size={12} /> Test (2 only)
              </button>
            ) : <span />}
            <div className="flex items-center gap-2">
              <button onClick={onClose} disabled={busy}
                className="text-[12px] px-3 py-1.5 rounded-md border hover:bg-gray-50"
                style={{ borderColor: '#E5E1DC', color: '#6B7280' }}>
                Close
              </button>
              <button onClick={() => dispatch(false)}
                disabled={busy || (requiresApproval && (loadingTargets || !targets?.total_targets))}
                className="text-[12px] px-3 py-1.5 rounded-md text-white inline-flex items-center gap-1 disabled:opacity-50"
                style={{ backgroundColor: isTeamPreview ? '#1C304A' : '#16653A' }}
                data-testid="newsletter-dispatch-go">
                {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                {busy ? 'Sending…' : (
                  isTeamPreview ? 'Send review copy' :
                  mode === 'bulk' ? 'Send to operator' :
                  `Push ${targets?.total_targets || '0'} drafts`
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ModeCard({ active, onClick, icon, title, subtitle, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className="text-left p-3 rounded-md border-2 hover:bg-gray-50 transition"
      style={{
        borderColor: active ? '#16653A' : '#E5E1DC',
        background: active ? '#F0FDF4' : '#FFFFFF',
      }}>
      <div className="text-[13px] font-semibold flex items-center gap-1.5"
        style={{ color: active ? '#16653A' : '#1B2A4A' }}>
        {icon} {title}
      </div>
      <div className="text-[11px] mt-1" style={{ color: '#6B7280' }}>{subtitle}</div>
    </button>
  );
}
